import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Play, Trash2, ListChecks, Radar, ExternalLink, KanbanSquare } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { PageHeader, EmptyState, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import CandidateCard from "@/components/CandidateCard";
import CandidatePanel from "@/components/CandidatePanel";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";
import { fmtDateTime } from "@/i18n/format";

const SEL = "h-8 rounded-md border bg-background px-2 text-xs";
const DISC = { ok: tk("Live discovery ran"), not_configured: tk("Discovery engine offline"), unavailable: tk("Discovery unavailable on your plan"),
  budget_exhausted: tk("Monthly monitor budget reached"), insufficient_credits: tk("Not enough credits"), provider_error: tk("Discovery temporarily failed — refunded") };
const FREQ = { off: tk("Off"), daily: tk("Daily"), weekly: tk("Weekly") };

function MonitorControls({ s, onChange }) {
  const { t } = useI18n();
  const patch = async (body) => {
    try { const { data } = await api.patch(`/saved-searches/${s.id}`, body); onChange(data); } catch (e) { toast.error(errorDetail(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs pt-3 mt-3 border-t" data-testid={`monitor-controls-${s.id}`}>
      <label className="flex items-center gap-1.5">{t("Monitor")}
        <select className={SEL} value={s.monitor_frequency} onChange={(e) => patch({ monitor_frequency: e.target.value })} data-testid={`monitor-frequency-${s.id}`}>
          {Object.entries(FREQ).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</select></label>
      <label className="flex items-center gap-1.5"><Switch checked={s.notify} onCheckedChange={(v) => patch({ notify: v })} data-testid={`monitor-notify-${s.id}`} />{t("Alerts")}</label>
      <label className="flex items-center gap-1.5"><Switch checked={s.include_discovery} onCheckedChange={(v) => patch({ include_discovery: v })} data-testid={`monitor-discovery-${s.id}`} />{t("Include live discovery")}</label>
      {s.include_discovery && <label className="flex items-center gap-1.5">{t("Budget")}
        <Input type="number" min={0} defaultValue={s.monitor_credit_budget} onBlur={(e) => patch({ monitor_credit_budget: Number(e.target.value) || 0 })} className="h-8 w-20 text-xs" data-testid={`monitor-budget-${s.id}`} />
        <span className="text-muted-foreground">{t("credits/month · used {{count}}", { count: s.monitor_credits_used })}</span></label>}
      {s.next_run_at && <span className="text-muted-foreground" data-testid={`monitor-next-run-${s.id}`}>{t("Next run {{date}}", { date: fmtDateTime(s.next_run_at) })}</span>}
    </div>
  );
}

function RunResults({ run }) {
  const { t } = useI18n();
  return (
    <div className="mt-6 space-y-2" data-testid="saved-run-results">
      <div className="text-sm">{t("{{count}} results · {{new}} new since last run", { count: run.total, new: run.new_count })}</div>
      {run.items.map((c, i) => <div key={c.id} className="relative">{c.is_new && <span className="absolute -left-2 top-3 z-10"><Pill tone="indigo">{t("NEW")}</Pill></span>}<CandidateCard c={c} idx={i} onSelect={() => {}} onSave={() => {}} onContact={() => {}} /></div>)}
    </div>
  );
}

function RunHistory({ id }) {
  const { t } = useI18n();
  const [runs, setRuns] = useState(null);
  useEffect(() => { api.get(`/saved-searches/${id}/runs`).then((r) => setRuns(r.data)); }, [id]);
  if (!runs) return null;
  return (
    <div className="mt-3 space-y-1.5" data-testid={`run-history-${id}`}>
      {!runs.length && <div className="text-xs text-muted-foreground">{t("No runs yet.")}</div>}
      {runs.map((r) => (
        <div key={r.id} className="text-xs rounded-md bg-muted px-3 py-2">
          <div className="flex flex-wrap gap-2 items-center"><Pill tone={r.trigger === "scheduled" ? "indigo" : "default"}>{t(r.trigger)}</Pill>{fmtDateTime(r.created_at)} · {t("{{count}} results · {{new}} new in catalog", { count: r.result_count, new: r.new_count })}
            {r.discovery_status && <span className="text-muted-foreground">· {t(DISC[r.discovery_status] || r.discovery_status)}{r.charged_credits ? ` (${t("{{count}} credits", { count: r.charged_credits })})` : ""}</span>}</div>
          {r.new_leads.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary mt-1 truncate"><ExternalLink className="w-3 h-3 shrink-0" />{l.title || l.url}</a>)}
        </div>
      ))}
    </div>
  );
}

export function SavedSearches() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [run, setRun] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/saved-searches").then((r) => setRows(r.data)); }, []);
  const replace = (s) => setRows((rs) => rs.map((x) => x.id === s.id ? s : x));
  const exec = async (s) => {
    try { const { data } = await api.post(`/saved-searches/${s.id}/run`); setRun(data); replace(data.saved_search); setOpen(null); } catch (e) { toast.error(errorDetail(e)); }
  };
  const del = async (s) => { await api.delete(`/saved-searches/${s.id}`); setRows(rows.filter((x) => x.id !== s.id)); };
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow={t("Monitoring")} title={t("Saved Searches")} desc={t("Schedule saved criteria to re-run daily or weekly. New profiles from your catalog — and optionally from live discovery, within a credit budget — trigger alerts.")} />
      {!rows.length && <EmptyState icon={Bookmark} title={t("No saved searches")} body={t("Save interpreted criteria from Talent Search.")} action={<Button asChild size="sm"><Link to="/app/search">{t("Open Talent Search")}</Link></Button>} />}
      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.id} className="card-surface p-4" data-testid={`saved-search-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0"><div className="font-medium">{s.title}</div>
                <div className="text-xs text-muted-foreground truncate">{s.query_text} · {t("last count {{count}}", { count: s.last_result_count })} · {s.last_run_at ? t("last run {{date}} · {{count}} new", { date: fmtDateTime(s.last_run_at), count: s.last_new_count }) : t("never run")}</div></div>
              <Pill tone={s.monitoring_status === "active" ? "green" : "default"} testid={`monitor-status-${s.id}`}><Radar className="w-3 h-3" />{t(FREQ[s.monitoring_status === "active" ? s.monitor_frequency : "off"] || s.monitor_frequency)}</Pill>
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === s.id ? null : s.id)} data-testid={`history-saved-${s.id}`}>{t("History")}</Button>
              <Button size="sm" variant="outline" onClick={() => exec(s)} data-testid={`run-saved-${s.id}`}><Play className="w-4 h-4" />{t("Run")}</Button>
              <button onClick={() => del(s)} aria-label={t("Delete")} data-testid={`delete-saved-${s.id}`} className="p-2 text-muted-foreground hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <MonitorControls s={s} onChange={replace} />
            {open === s.id && <RunHistory id={s.id} />}
          </div>
        ))}
      </div>
      {run && <RunResults run={run} />}
    </div>
  );
}

function ListDetail({ sel, projects, onChanged, onOpen }) {
  const { t } = useI18n();
  const [pid, setPid] = useState("");
  const remove = async (cid) => { await api.delete(`/lists/${sel.id}/members/${cid}`); onChanged(); };
  const toProject = async () => {
    try { const { data } = await api.post(`/lists/${sel.id}/to-project`, { project_id: pid }); toast.success(t("{{count}} candidates added to the project", { count: data.added })); } catch (e) { toast.error(errorDetail(e)); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <select className={SEL} value={pid} onChange={(e) => setPid(e.target.value)} data-testid="list-project-select"><option value="">{t("Choose a project…")}</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select>
        <Button size="sm" variant="outline" disabled={!pid || !sel.candidates.length} onClick={toProject} data-testid="list-to-project-button"><KanbanSquare className="w-4 h-4" />{t("Add all to project")}</Button>
      </div>
      {sel.candidates.length ? sel.candidates.map((c, i) => (
        <div key={c.id} className="flex gap-2 items-start"><div className="flex-1"><CandidateCard c={c} idx={i} onSelect={() => onOpen(c.id)} onSave={() => {}} onContact={() => onOpen(c.id)} /></div>
          <button onClick={() => remove(c.id)} aria-label={t("Remove")} className="p-2 text-muted-foreground hover:text-red-600" data-testid={`remove-from-list-${i}`}><Trash2 className="w-4 h-4" /></button></div>
      )) : <EmptyState title={t("This list is empty")} body={t("Save candidates from Talent Search.")} />}
    </div>
  );
}

export function TalentLists() {
  const { t } = useI18n();
  const [panel, setPanel] = useState(null);
  const [lists, setLists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sel, setSel] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/lists").then((r) => setLists(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);
  const create = async () => { await api.post("/lists", { name, shared: true }); setName(""); load(); };
  const open = (l) => api.get(`/lists/${l.id}`).then((r) => setSel(r.data));
  const toggleShared = async (l) => { await api.patch(`/lists/${l.id}`, { shared: !l.shared }); load(); };
  const del = async (l) => {
    try { await api.delete(`/lists/${l.id}`); if (sel?.id === l.id) setSel(null); load(); } catch (e) { toast.error(errorDetail(e)); }
  };
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow={t("Shortlists")} title={t("Talent Lists")} desc={t("Shared candidate lists for your organization.")} actions={<><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("New list")} className="h-9 w-48" data-testid="new-list-input" /><Button size="sm" disabled={!name.trim()} onClick={create} data-testid="create-list-button">{t("Create")}</Button></>} />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          {!lists.length && <EmptyState icon={ListChecks} title={t("No lists yet")} />}
          {lists.map((l) => (
            <div key={l.id} className={`card-surface p-3 flex items-center gap-2 ${sel?.id === l.id ? "border-primary" : ""}`}>
              <button onClick={() => open(l)} data-testid={`list-${l.id}`} className="flex-1 text-left min-w-0"><div className="font-medium text-sm truncate">{l.name}</div><div className="text-xs text-muted-foreground">{t("{{count}} candidates", { count: l.count })}</div></button>
              <button onClick={() => toggleShared(l)} data-testid={`toggle-shared-${l.id}`}><Pill tone={l.shared ? "indigo" : "default"}>{l.shared ? t("shared") : t("private")}</Pill></button>
              <button onClick={() => del(l)} aria-label={t("Delete")} data-testid={`delete-list-${l.id}`} className="p-1 text-muted-foreground hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2">{sel ? <ListDetail sel={sel} projects={projects} onOpen={setPanel} onChanged={() => { open(sel); load(); }} /> : <EmptyState icon={ListChecks} title={t("Select a list")} />}</div>
      </div>
      {panel && <aside className="fixed inset-y-0 right-0 z-40 w-full sm:w-[440px] bg-background border-l shadow-xl overflow-y-auto" data-testid="list-candidate-panel"><CandidatePanel id={panel} onClose={() => setPanel(null)} /></aside>}
    </div>
  );
}
